import { loadScript } from "../../../../utils";
import { inDebugMode } from "../../../../utils/debug";
import promiseWithTimeout from "../../../../utils/timeout";
import { PageType } from "../../../dataLayer";
import { filterRelevance } from "../../../meta-api";
import requestArticleMetadata from "../../../meta-api/article-meta";
import Service from "../../../service";
import { cleanEmptyValues } from "../../dmp-format";
const defaultRelevancyLimits = {
    iabTier1: {
        min: 0,
        max: 100,
    },
    iabTier2: {
        min: 10,
        max: 100,
    },
    semantics: {
        min: 10,
        max: 100,
    },
};
class AlmaOneplusXService extends Service {
    constructor(context, config) {
        super("AlmaOneplusX", context);
        this.hasConsent = false;
        this.consentListenerSet = false;
        this.scriptReady = false;
        this.userIdentified = false;
        this.configuration = {
            ...config,
            defaults: {
                ...config.defaults,
                relevancy: {
                    relevancyLimits: config.defaults?.relevancy?.relevancyLimits ??
                        defaultRelevancyLimits,
                },
            },
        };
    }
    async init() {
        this.logger.logInfo(`Started`);
    }
    collectData() {
        const collect = async () => {
            this.logger.logInfo(`Data collection requested`);
            let customProps;
            try {
                this.logger.logInfo("Checking for preDataCollection hook");
                if (this.configuration.preDataCollection) {
                    this.logger.logInfo("Found preDataCollection, awaiting it");
                    const result = await this.configuration.preDataCollection(this.context);
                    if (typeof result === "object" && "reason" in result) {
                        this.logger.logInfo(`DMP data collection cancelled: ${result.reason}`);
                        return;
                    }
                    this.logger.logInfo(`preDataCollection returned ${JSON.stringify(result, null, 2)}`);
                    customProps = result.props;
                }
            }
            catch (e) {
                this.logger.logError(`Error occured when calling preDataCollection: ${e}`);
            }
            await this.setConsent();
            if (this.hasConsent === false) {
                this.logger.logInfo("We lack consent from user, do nothing");
                return;
            }
            if (this.scriptReady === false) {
                await this.initScript();
            }
            try {
                if (this.userIdentified === false) {
                    this.logger.logInfo(`Trying to identify user first...`);
                    this.identifyUser();
                }
            }
            catch (e) {
                this.logger.logError(`Identify failed: ${e}`);
            }
            try {
                this.context.core?.recordEvent("1plusx.snapshot.start", window.performance.now());
                const snapshot = await this.createDataLayerSnapshot(customProps);
                this.context.core?.recordEvent("1plusx.snapshot.end", window.performance.now());
                if (!snapshot) {
                    this.logger.logError(`Cancelling data collection - could not form DMP data`);
                    return;
                }
                await this.trackPageView(snapshot);
            }
            catch (e) {
                this.logger.logError(`Data collection failed due to: ${e}`);
            }
        };
        const collectionTimeout = this.configuration.defaults?.waitBeforeCollect ?? 0;
        if (this.collectionRequestTimeout) {
            this.logger.logInfo(`Clearing stale page view request`);
            window.clearTimeout(this.collectionRequestTimeout);
            this.collectionRequestTimeout = undefined;
        }
        this.logger.logInfo(`Pageview requested, executing in ${collectionTimeout} ms`);
        this.context.core?.recordEvent("1plusx.pageview_requested", performance.now());
        this.collectionRequestTimeout = window.setTimeout(collect, collectionTimeout);
    }
    async getSegmentsAsync() {
        this.logger.logInfo(`Current segments requested`);
        return promiseWithTimeout(200, new Promise((resolve, reject) => {
            this.safeOneplusXInvoke("onProfile", (error, profile) => {
                if (error || typeof profile === "string") {
                    reject(error ?? profile);
                }
                resolve(profile);
            }, { awaitConsent: true });
        }), "Timeout of 200 ms exceeded while fetching profile");
    }
    getSegmentsSync() {
        this.logger.logInfo(`Current segments requested (sync)`);
        throw new Error("Not yet implemented");
    }
    async createDataLayerSnapshot(customProps) {
        this.logger.logInfo(`Create data layer snapshot`);
        if (!this.context.services.dataLayerService) {
            throw new Error("Data layer service not available");
        }
        const { dataLayer } = this.context.services.dataLayerService;
        const dmpBsStart = window.performance.now();
        const isBrandSafe = await this.context.services.brandsafetyService?.isSafe("dmp");
        if (typeof isBrandSafe === "boolean") {
            this.context.core?.recordEvent("1plusx.bs.start", dmpBsStart);
            this.context.core?.recordEvent("1plusx.bs.end", window.performance.now());
        }
        let filteredMetaData = {};
        if (dataLayer.pageType === PageType.Article &&
            typeof dataLayer.cid === "string") {
            const limits = this.configuration.defaults?.relevancy?.relevancyLimits ??
                defaultRelevancyLimits;
            this.logger.logInfo(`Filtering DMP meta: ${JSON.stringify(limits, null, 2)}`);
            const metaData = await requestArticleMetadata(this.context);
            filteredMetaData = {
                iabTier1: filterRelevance(metaData?.iabTier1 ?? [], limits.iabTier1.min, limits.iabTier1.max),
                iabTier2: filterRelevance(metaData?.iabTier2 ?? [], limits.iabTier2.min, limits.iabTier2.max),
                leikiHighDef: filterRelevance(metaData?.leikiHighDef ?? [], limits.semantics.min, limits.semantics.max),
            };
        }
        const defaultPageProps = [
            {
                name: "iabTier1",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.iabTier1;
                },
            },
            {
                name: "iabTier2",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.iabTier2;
                },
            },
            {
                name: "semantics",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.leikiHighDef;
                },
            },
            {
                name: "primaryCategory",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.primaryCategory;
                },
            },
            {
                name: "subCategory",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.subCategory1;
                },
            },
            {
                name: "tags",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.pageTags;
                },
            },
            {
                name: "pageType",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.pageType;
                },
            },
        ];
        try {
            // Split category to we can merge any extra stuff
            const { page = [], ...otherProps } = { ...customProps };
            const pageviewData = {
                page: [...defaultPageProps, ...(page ?? [])],
                ...otherProps,
            };
            // Resolve all and reduce to key-value object
            const cleanData = (await Promise.all(Object.entries(pageviewData).map(async ([k, v]) => {
                return { [k]: await this.resolveDataLayer(v, isBrandSafe) };
            }))).reduce((k, v) => {
                const propKey = Object.keys(v)[0];
                return { ...k, [propKey]: v[propKey] };
            }, {});
            this.logger.logInfo(`Data layer snapshot: ${JSON.stringify(cleanData, null, 2)}`);
            return cleanData;
        }
        catch (e) {
            this.logger.logInfo(`DMP Data layer snapshot failed: ${e}`);
        }
        return undefined;
    }
    async resolveDataLayer(dmpProperty, isBrandSafe) {
        this.logger.logInfo(`Resolving partial DMP data layer. ${dmpProperty.length} props`);
        const brandsafetyfyValue = (value) => {
            if (typeof value === "string") {
                return `${value}-bs`;
            }
            return value;
        };
        const resolveProps = async (props) => {
            const values = (await Promise.all(props.map(async (prop) => {
                try {
                    let result = await prop.resolve(this.context);
                    if (prop.brandSafety && isBrandSafe === false) {
                        if (Array.isArray(result)) {
                            result = result.map(brandsafetyfyValue);
                        }
                        else {
                            result = [result].map(brandsafetyfyValue)?.[0];
                        }
                    }
                    return {
                        key: prop.name,
                        value: result,
                    };
                }
                catch (e) {
                    this.logger.logError(`Unable to resolve ${prop.name} due to ${e}`);
                    return { key: prop.name, value: undefined };
                }
            }))).reduce((acc, prop) => {
                return { ...acc, [prop.key]: prop.value };
            }, {});
            return cleanEmptyValues(values);
        };
        return resolveProps(dmpProperty);
    }
    async trackPageView(snapshot) {
        if (snapshot) {
            const { page, search, product } = snapshot;
            if (page && Object.keys(page).length > 0) {
                this.sendEvent("pageInfo", { ...page });
            }
            if (search && Object.keys(search).length > 0) {
                this.sendEvent("searchTerm", { ...search });
            }
            if (product && Object.keys(product).length > 0) {
                this.sendEvent("productInfo", { ...product });
            }
        }
    }
    sendEvent(name, payload) {
        this.safeOneplusXInvoke("send", name, Object.entries(payload).reduce((obj, [k, v]) => {
            const value = Array.isArray(v) ? v.join("|") : v;
            if (value.length > 0) {
                return { ...obj, [k]: value };
            }
            return obj;
        }, {}));
    }
    identifyUser() {
        if (this.userIdentified === false) {
            const dl = this.context.services.dataLayerService?.dataLayer;
            // email_sha256
            if (dl?.cdime &&
                typeof dl.cdime === "string" &&
                dl.cdime.length > 0 &&
                dl.cdime.match(/^[0-9a-f]{64}$/i)) {
                this.safeOneplusXInvoke("send", "userInfo", { email_sha256: dl.cdime });
                this.userIdentified = true;
            }
            else {
                this.logger.logInfo(`No cdime available`);
            }
        }
        else {
            this.logger.logInfo(`User already identified`);
        }
    }
    async setConsent() {
        const consent = await this.context.services.almaCmpService?.getPublisherConsents();
        const hasConsent = (c) => {
            if (c?.standard[1] === true &&
                c.standard[3] === true &&
                c.standard[4] === true &&
                c.custom[1] === true &&
                c.specialFeature[2] === true &&
                c.vendors[92] === true) {
                return true;
            }
            return false;
        };
        if (!this.consentListenerSet) {
            const consentChangedEvent = "alma_cmp_v2_has_optedin";
            this.logger.logInfo(`Listening for changes with ${consentChangedEvent}`);
            window.addEventListener(consentChangedEvent, async (event) => {
                const result = hasConsent(event.detail);
                if (this.hasConsent !== result) {
                    this.logger.logInfo(`Consent changed, update`);
                    this.hasConsent = result;
                    window.permutive?.consent({
                        optin: result,
                        token: "CONSENT_CAPTURED",
                    });
                    if (result === true && this.scriptReady === false) {
                        await this.initScript();
                    }
                }
            }, false);
            this.consentListenerSet = true;
        }
        this.hasConsent = hasConsent(consent);
        this.logger.logInfo(`Consent: ${this.hasConsent}`);
    }
    async initScript() {
        this.logger.logInfo(`Init 1plusX`);
        if (!this.configuration.clientId) {
            throw new Error("Missing clientId");
        }
        // Create 1plusX Global
        const onePlusXGlobalInterface = function (...args) {
            window.ope?.q.push(args);
        };
        Object.assign(onePlusXGlobalInterface, { q: [] }, window.ope);
        window.ope = onePlusXGlobalInterface;
        this.safeOneplusXInvoke("init", "tcf2");
        this.scriptReady = true;
        loadScript(`https://cdn.opecloud.com/ope-${this.configuration.clientId}${inDebugMode() ? "-debug" : ""}.js`, window);
    }
    safeOneplusXInvoke(command, ...args) {
        if (window.ope && typeof window.ope === "function") {
            this.logger.logInfo(`Invoking 1plusX Global with args: ${Array.from(args)
                .map((a) => JSON.stringify(a, null, 2))
                .join(", ")}`);
            window.ope(this.configuration.clientId, command, ...args);
        }
        else {
            this.logger.logError(`window.ope does not exist`);
        }
    }
}
export default AlmaOneplusXService;
//# sourceMappingURL=1plusx.js.map